/**
 * Le catalogue des connecteurs proposés à la connexion, décrit une fois.
 *
 * ## Ce qu'il remplace
 *
 * Les onze connecteurs étaient du JSX recopié : une carte de trente lignes par connecteur, dans
 * la modale de l'écran Connecteurs (`App.js`) **et** dans celle du Gestionnaire
 * (`KanbanView.jsx`) — soit deux listes à tenir d'accord, qui avaient déjà divergé par leurs
 * couleurs de survol. Le parcours d'installation en aurait fait une troisième.
 *
 * Ce fichier ne décrit que **ce qui distingue un connecteur d'un autre** : son nom, son logo, et
 * la façon dont on s'y branche. La mise en forme reste à chaque écran, qui n'a pas les mêmes
 * contraintes — une grille de cartes ici, une étape plein écran là.
 *
 * ## Les deux manières de se brancher
 *
 * - `oauth` : on quitte l'application pour le fournisseur, qui nous rend la main. `segment` est
 *   le morceau d'URL de la route d'autorisation (`/api/auth/<segment>`).
 * - `formulaire` : tout se passe sur place, par saisie d'une clé ou d'un jeton, selon le même
 *   protocole en deux temps — `POST …/test` valide et énumère, `POST …/connect` enregistre.
 *
 * ⚠️ Les connecteurs à formulaire **créent leurs comptes à la connexion**, contrairement aux
 * connecteurs OAuth où le choix des comptes est une étape distincte : une clé ChatGPT Ads vaut un
 * compte, un jeton Awin en apporte plusieurs, Matomo comme Piano importent leurs sites, et une
 * clé PrestaShop ouvre les boutiques de son installation. C'est `comptesALaConnexion` qui le dit,
 * pour que le parcours n'attende pas une étape qui a déjà eu lieu.
 *
 * ## Ce qui n'y figure pas, et pourquoi
 *
 * Google Merchant Center ne se branche que depuis les écrans Shopping et Optimiseur de flux, où
 * le choix du compte marchand a un sens ; Google Sheets et les services météo sont réservés aux
 * plans payants et activés ailleurs. Les ajouter ici les ferait apparaître dans un parcours
 * d'installation où ils n'ont rien à faire.
 */

import googleAdsLogo from '../logos/google-ads.png';
import microsoftAdsLogo from '../logos/microsoft-ads.png';
import metaAdsLogo from '../logos/meta-ads.png';
import pinterestAdsLogo from '../logos/pinterest-ads.png';
import tiktokAdsLogo from '../logos/tiktok-ads.png';
import googleAnalyticsLogo from '../logos/google-analytics.svg';
import searchConsoleLogo from '../logos/google-search-console.png';
import tagManagerLogo from '../logos/google-tag-manager.png';
import matomoLogo from '../logos/matomo.png';
import pianoLogo from '../logos/piano-analytics.png';
import chatgptAdsLogo from '../logos/chatgpt-ads.png';
import awinLogo from '../logos/awin.png';
import prestashopLogo from '../logos/prestashop.png';

export const CONNECTEURS = [
  { cle: 'GOOGLE_ADS', libelle: 'Google Ads', logo: googleAdsLogo, mode: 'oauth', segment: 'google-ads' },
  { cle: 'MICROSOFT_ADS', libelle: 'Microsoft Ads', logo: microsoftAdsLogo, mode: 'oauth', segment: 'microsoft-ads' },
  { cle: 'FACEBOOK_ADS', libelle: 'Meta Ads', logo: metaAdsLogo, mode: 'oauth', segment: 'facebook-ads' },
  { cle: 'PINTEREST_ADS', libelle: 'Pinterest Ads', logo: pinterestAdsLogo, mode: 'oauth', segment: 'pinterest-ads' },
  { cle: 'TIKTOK_ADS', libelle: 'TikTok Ads', logo: tiktokAdsLogo, mode: 'oauth', segment: 'tiktok-ads' },
  { cle: 'GOOGLE_ANALYTICS', libelle: 'Google Analytics', logo: googleAnalyticsLogo, mode: 'oauth', segment: 'google-analytics' },
  { cle: 'GOOGLE_SEARCH_CONSOLE', libelle: 'Search Console', logo: searchConsoleLogo, mode: 'oauth', segment: 'google-search-console' },
  { cle: 'GOOGLE_TAG_MANAGER', libelle: 'Google Tag Manager', logo: tagManagerLogo, mode: 'oauth', segment: 'google-tag-manager' },
  { cle: 'MATOMO', libelle: 'Matomo', logo: matomoLogo, mode: 'formulaire', comptesALaConnexion: true },
  { cle: 'PIANO_ANALYTICS', libelle: 'Piano Analytics', logo: pianoLogo, mode: 'formulaire', comptesALaConnexion: true },
  { cle: 'OPENAI_ADS', libelle: 'ChatGPT Ads', logo: chatgptAdsLogo, mode: 'formulaire', comptesALaConnexion: true },
  { cle: 'AWIN', libelle: 'Awin', logo: awinLogo, mode: 'formulaire', comptesALaConnexion: true },
  { cle: 'PRESTASHOP', libelle: 'PrestaShop', logo: prestashopLogo, mode: 'formulaire', comptesALaConnexion: true }
];

/** Le connecteur portant cette clé, ou `undefined`. */
export function connecteurParCle(cle) {
  return CONNECTEURS.find((c) => c.cle === cle);
}

/**
 * L'URL d'autorisation d'un connecteur OAuth.
 *
 * `retour` nomme l'écran à rouvrir au retour — le serveur ne connaît que les destinations
 * déclarées dans `backend/src/lib/retourOAuth.js`, et retombe sur l'écran Connecteurs pour toute
 * autre valeur.
 */
export function urlAutorisation(apiUrl, connecteur, token, retour = 'connectors') {
  return `${apiUrl}/api/auth/${connecteur.segment}?token=${token}&returnTo=${retour}`;
}

export default CONNECTEURS;
